import { zonedTimeToUtc } from 'date-fns-tz';

export type PendingBookingUrgency = 'due' | 'soon';

export const PENDING_BOOKING_SOON_WINDOW_MS = 48 * 60 * 60 * 1000;

function resolveTimeZone(timeZone?: string): string {
  const trimmed = timeZone?.trim();
  if (trimmed) {
    return trimmed;
  }
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function normalizeTime(time: string): string | null {
  if (!time?.trim()) {
    return null;
  }
  const parts = time.trim().split(':');
  if (parts.length < 2) {
    return null;
  }
  const hh = parts[0].padStart(2, '0');
  const mm = parts[1].padStart(2, '0');
  const ss = parts[2]?.padStart(2, '0') ?? '00';
  return `${hh}:${mm}:${ss}`;
}

function parseAppointmentMs(date: string, time: string, timeZone: string): number | null {
  if (!date?.trim()) {
    return null;
  }
  const normalizedTime = normalizeTime(time);
  if (!normalizedTime) {
    return null;
  }
  try {
    const ms = zonedTimeToUtc(`${date.trim()}T${normalizedTime}`, timeZone).getTime();
    return Number.isNaN(ms) ? null : ms;
  } catch {
    return null;
  }
}

export function getPendingBookingUrgency(
  date: string,
  time: string,
  timeZone?: string,
  nowMs: number = Date.now()
): PendingBookingUrgency | null {
  const appointmentMs = parseAppointmentMs(date, time, resolveTimeZone(timeZone));
  if (appointmentMs === null) {
    return null;
  }

  if (appointmentMs < nowMs) {
    return 'due';
  }
  if (appointmentMs - nowMs <= PENDING_BOOKING_SOON_WINDOW_MS) {
    return 'soon';
  }
  return null;
}
